import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import { getColor } from 'common/constants/_colors';
import slackLogoColored from 'images/logos/slack-logo.svg';
import withSlackReAuthorization from 'components/HOC/ReAuthorizationCheckerHOC';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING, SUPPORTED_CHAT_TOOLS } from 'common/constants/constants';

import {
  SlackMissingScopesState,
  SlackInvalidTokenState,
  ConnectedState,
  SlackTeamConnectedState,
  NotConnectedState
} from './SlackConnectedTopBarStates';
import { SLACK_STATUSES } from 'common/services/slack.service';

export const StyledSVG = styled.img`
  ${({ width }) => `width: ${width ? width : '2rem'}`};
  ${({ height }) => `height: ${height ? height : '2rem'}`};
  ${({ margin }) => `margin: ${margin ? margin : '0 1rem 0 0.5rem'}`};
`;

export const getSlackStatus = ({
  isSlackConnected,
  slackInvalidToken,
  slackMissingScopes
}) => {
  if (slackInvalidToken || slackMissingScopes) {
    return SLACK_STATUSES.RE_AUTH_NEEDED;
  }

  return isSlackConnected
    ? SLACK_STATUSES.CONNECTED
    : SLACK_STATUSES.NOT_CONNETED;
};

const getConnectedTopBarStyle = slackStatus => {
  switch (slackStatus) {
    case SLACK_STATUSES.NOT_CONNETED:
      return {
        backgroundColor: 'catskillGray',
        border: 'catskillWhite'
      };
    case SLACK_STATUSES.RE_AUTH_NEEDED:
      return {
        backgroundColor: 'bridalHeath',
        border: 'serenade'
      };
    case SLACK_STATUSES.CONNECTED:
      return {
        backgroundColor: 'fetaLight',
        border: 'feta'
      };
    default:
      return {};
  }
};

const SlackConnectedTopBar = ({
  toggleNotificationsForTeam,
  currentTeam,
  isOrgSettings,
  isSlackConnected,
  isNotificationsChecked,
  enableReAuthorizationButton,
  slackMissingScopes,
  slackInvalidToken,
  connectToSlack,
  contactSupport,
  teamSlackChannel
}) => {
  const { CONNECTED, NOT_CONNETED, RE_AUTH_NEEDED } = SLACK_STATUSES;
  const slackStatus = getSlackStatus({
    isSlackConnected,
    slackInvalidToken,
    slackMissingScopes
  });
  const isTeamSettings = !isOrgSettings;

  const connectedTopBarStyle = getConnectedTopBarStyle(slackStatus);

  useEffect(() => {
    if (enableReAuthorizationButton)
      TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
        page: TRACKING.PAGES.SETTINGS,
        page_tab: TRACKING.PAGE_TABS.ORG_SLACK,
        show_slack_upgrade: true
      });
  }, []);

  return (
    <Flex
      padding='small'
      mb='medium'
      minHeight='3.4rem'
      style={{
        backgroundColor: getColor(connectedTopBarStyle.backgroundColor),
        border: `1px solid ${getColor(connectedTopBarStyle.border)}`
      }}
    >
      <Flex grow='1' items='center'>
        <StyledSVG src={slackLogoColored} alt={SUPPORTED_CHAT_TOOLS.SLACK} />
        <Text size='x-large'>Slack</Text>
        {slackStatus === CONNECTED && (
          <ConnectedState
            teamSlackChannel={teamSlackChannel}
            isTeamSettings={isTeamSettings}
          />
        )}
        {slackStatus === NOT_CONNETED && <NotConnectedState />}

        {slackStatus === RE_AUTH_NEEDED && (
          <SlackInvalidTokenState
            connectToSlack={connectToSlack}
            contactSupport={contactSupport}
            isReAuthAllowed={enableReAuthorizationButton}
          />
        )}
        {slackStatus === RE_AUTH_NEEDED && slackMissingScopes && (
          <SlackMissingScopesState connectToSlack={connectToSlack} />
        )}
        {isTeamSettings && slackStatus === CONNECTED && teamSlackChannel && (
          <SlackTeamConnectedState
            isNotificationsChecked={isNotificationsChecked}
            toggleNotificationsForTeam={event =>
              toggleNotificationsForTeam(currentTeam, event.target.checked)
            }
            notConnected={false}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default withSlackReAuthorization(SlackConnectedTopBar);
